import h from '../global/create-element.js';
import getState from '../global/state.js';
import { showToast } from '../elements/toasts.js';

const liveChatCheckClass = 'wagtail-userbar-reset';
const liveChatClass = 'LiveChat';
const liveChatOpenClass = 'LiveChat--open';
const liveChatEditorClass = 'LiveChat--isEditor';
const liveChatTriggerClass = 'js-LiveChat__Trigger';
const liveChatContentClass = 'LiveChat__Content';
const liveChatEmbedClass = 'LiveChat__Embed';
const liveChatErrorClass = 'LiveChat__Error';
const liveChatActionClass = 'LiveChat__Action';
const liveChatActionLoadingClass = 'LiveChat__Action--loading';

async function fetchLiveChatEmbedUrl() {
  const response = await fetch(`/matrix-chat/embed-url/${getState('liveChatPageId')}`);
  if (response.status >= 400 && response.status <= 599) {
    throw new Error(response.statusText);
  }
  return response.text();
}

async function renderLiveChatEmbed($content) {
  try {
    const embedUrl = await fetchLiveChatEmbedUrl();
    const $embed = h('iframe', {
      class: liveChatEmbedClass,
      src: embedUrl
    });
    $content.appendChild($embed);
    return $embed;
  } catch (error) {
    return renderLiveChatError($content);
  }
}

function renderLiveChatError($content) {
  const $embed = h('p', {
    class: liveChatErrorClass
  }, 'Something went wrong while loading the live chat. Please reload the page to try again.');
  $content.appendChild($embed);
  return $embed;
}

function attachLiveChatEmbedListeners($embed) {
  $embed.addEventListener('message', (event) => {
    const { type, data } = event.data;
    if (type === 'notification') {
      const { title, ...options } = data;
      const notification = new window.Notification(title, options);
      notification.addEventListener('click', () => {
        window.focus();
        notification.close();
      });
    }
  });
}

async function toggleLiveChat($element) {
  $element.classList.toggle(liveChatOpenClass);
  if ($element.classList.contains(liveChatOpenClass)) {
    const $content = $element.querySelector(`.${liveChatContentClass}`);
    if ($content.querySelector(`.${liveChatEmbedClass}`)) {
      return;
    }
    $content.querySelector(`.${liveChatErrorClass}`)?.remove();

    const $embed = await renderLiveChatEmbed($content);
    if ($embed.classList.contains(liveChatEmbedClass)) {
      attachLiveChatEmbedListeners($embed);
    }
  }
}

async function triggerLiveChatAction($element, $action) {
  const action = $action.dataset.action;
  if (action === 'maximize') {
    $action.classList.add(liveChatActionLoadingClass);
    await openMaximizedLiveChat();
    $action.classList.remove(liveChatActionLoadingClass);
  }
}

async function openMaximizedLiveChat() {
  const pageUrl = await fetchLiveChatEmbedUrl();
  window.open(pageUrl, '_blank');
}

function enableLiveChatEditorFeatures($element) {
  // We use the presence of the Wagtail FAB to determine if the current user is
  // an editor of the current page or not.
  const $check = document.querySelector(`.${liveChatCheckClass}`);
  if (!$check) {
    return;
  }
  $element.classList.add(liveChatEditorClass);
}

function requireAuthenticatedUser($element) {
  if (getState('user') === 'True') {
    return true;
  }

  let message = 'Sorry, you cannot access the live chat at the moment.';
  const currentUrl = window.location.pathname + window.location.search;
  const loginUrl = getState('loginUrl');
  const registerUrl = getState('registerUrl');

  if (loginUrl || registerUrl) {
    message = `Please `;
    let loginUrlWithRedirect = loginUrl;
    if (loginUrlWithRedirect) {
      loginUrlWithRedirect += `?next=${currentUrl}`
    }
    let registerUrlWithRedirect = registerUrl;
    if (registerUrlWithRedirect) {
      registerUrlWithRedirect += `?next=${currentUrl}`
    }
    if (loginUrlWithRedirect) {
      message += `<a href="${loginUrlWithRedirect}">sign in</a> `;
      if (registerUrlWithRedirect) {
        message += `or <a href="${registerUrlWithRedirect}">register</a> `;
      }
    } else {
      message += `<a href="${registerUrlWithRedirect}">register</a> `;
    }
    message += 'to access the live chat.';
  }

  showToast(message, {
    iconUrl: '/static/images/ic-alert-circle-dark.svg'
  });
  return false;
}

export function initializeLiveChat() {
  const $element = document.querySelector(`.${liveChatClass}`);
  if (!$element) {
    return;
  }

  enableLiveChatEditorFeatures($element);

  $element.addEventListener('click', (event) => {
    const $target = event.target.closest(`.${liveChatTriggerClass}, .${liveChatActionClass}`);
    if (!$target) {
      return;
    }

    if ($target.classList.contains(liveChatTriggerClass)) {
      if (requireAuthenticatedUser()) {
        toggleLiveChat($element);
      }
    } else if ($target.classList.contains(liveChatActionClass)) {
      triggerLiveChatAction($element, $target);
    }
  });
}
